import { useState, useEffect, useRef, FC, ReactNode } from 'react';
import classNames from 'classnames';
import { Button } from './Button';
type Position = 'top' | 'right' | 'bottom' | 'left';
type Mode = 'click' | 'hover';

interface IDropdownProps {
  className?: string;
  trigger?: ReactNode;
  position?: Position;
  children: ReactNode;
  changeState?: (state: boolean) => void;
  popoverClassName?: string;
  isOpen: boolean;
  mode?: Mode;
}

export const Dropdown: FC<IDropdownProps> = ({
  className,
  trigger,
  position = 'bottom',
  children,
  isOpen = false,
  changeState,
  popoverClassName,
  mode = 'click',
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(isOpen);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleDropdownToggle = () => {
    if (mode === 'click') {
      setIsDropdownOpen(!isDropdownOpen);
      if (changeState) changeState(!isDropdownOpen);
    }
  };

  const handleMouseEnter = () => {
    if (mode === 'hover') {
      // 타이머가 있다면 클리어
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setIsDropdownOpen(true);
      if (changeState) changeState(true);
    }
  };
  const handleMouseLeave = () => {
    if (mode === 'hover') {
      // 지연 시간을 주어 메뉴로 이동할 시간을 확보
      timeoutRef.current = setTimeout(() => {
        setIsDropdownOpen(false);
        if (changeState) changeState(false);
      }, 150); // 150ms의 지연 시간
    }
  };
  const closeDropdown = () => {
    setIsDropdownOpen(false);
    if (changeState) changeState(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (mode === 'click' && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      closeDropdown();
    }
  };

  useEffect(() => {
    setIsDropdownOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    window.addEventListener('click', handleClickOutside);
    return () => {
      window.removeEventListener('click', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    const handleKeyPress = (event: { key: string }) => {
      if (event.key === 'Escape') {
        closeDropdown();
      }
    };

    if (isDropdownOpen) {
      window.addEventListener('keydown', handleKeyPress);
    } else {
      window.removeEventListener('keydown', handleKeyPress);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDropdownOpen]);

  useEffect(() => {
    if (mode === 'click' && triggerRef.current) {
      const buttonOrLink = triggerRef.current.querySelector('button, a') as
        | HTMLButtonElement
        | HTMLAnchorElement
        | undefined;
      if (buttonOrLink) {
        buttonOrLink.onclick = handleDropdownToggle;
        buttonOrLink.setAttribute('aria-expanded', isDropdownOpen.toString());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDropdownOpen, trigger, mode]);
  
  // 컴포넌트가 언마운트될 때 타이머 클리어
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className={classNames('ds-dropdown', className)}
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div ref={triggerRef}>
        {trigger ? (
          trigger
        ) : (
          <Button
            className={classNames('ds-dropdown-trigger')}
            onClick={mode === 'click' ? handleDropdownToggle : undefined}
            aria-expanded={isDropdownOpen}
          >
            Click
          </Button>
        )}
      </div>
      {isOpen && (
        <div
          className={classNames(
            'ds-popover',
            // position이 bottom 이 아니면, popover-position.charAt(0) 한다는 뜻, type때문에 길어짐
            {
              [`popover-${typeof position === 'string' ? position.charAt(0) : ''}`]:
                typeof position === 'string' && position !== 'bottom',
            },
            popoverClassName,
          )}
          data-state={isDropdownOpen ? 'show' : 'hide'}
        >
          {children}
        </div>
      )}
    </div>
  );
};
